import pLimit from 'p-limit';
import { listEvents, resetPace, insertEvent, patchEvent, deleteEvent } from './googleCalendar';
import { IMPORT_DAYS_AHEAD, EXPORT_DAYS_BACK } from './googleConfig';
import { toDateStr, fmtHM, secToClock, diffSeconds } from './timeUtils';

let idCounter = 0;
const newId = () => Date.now() * 1000 + (idCounter++ % 1000);

const keyOf = (calendarId, eventId) => `${calendarId}::${eventId}`;
const alive = (t) => !t.deletedAt;

/**
 * 取り込み対象の期間。
 * 明日から IMPORT_DAYS_AHEAD 日ぶん（両端を含む）。
 * 例）今日が 8/13、14日ぶんなら 8/14 〜 8/27。
 */
export const importWindow = () => {
  const from = new Date();
  from.setDate(from.getDate() + 1);
  from.setHours(0, 0, 0, 0);

  const to = new Date(from);
  to.setDate(to.getDate() + IMPORT_DAYS_AHEAD - 1);
  to.setHours(23, 59, 59, 999);

  return { from, to, fromStr: toDateStr(from), toStr: toDateStr(to) };
};

/** 書き出し対象の期間（EXPORT_DAYS_BACK日前 〜 今日） */
export const exportWindow = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - EXPORT_DAYS_BACK);
  return { fromStr: toDateStr(from), toStr: toDateStr(to) };
};

/**
 * 何回まで消しにいくか。
 * 通らないものを残し続けると、更新のたびに待ち時間が増えていく。
 */
const MAX_DELETE_TRIES = 3;

/**
 * 削除待ちの後始末。
 * すでにカレンダー側に無い場合（404/410）は済んだものとして扱う。
 * 何度やっても通らないものは、回数で打ち切って列から外す。
 */
export const flushDeletes = async (items = []) => {
  // Google API の pace() 状態をリセット
  resetPace();

  if (items.length === 0) return [];

  const limit = pLimit(3);
  const results = await Promise.all(
    items.map((it) =>
      limit(async () => {
        if (!it || !it.calendarId || !it.eventId) return null;

        try {
          await deleteEvent(it.calendarId, it.eventId);
          return null; // 消せた
        } catch (e) {
          if (e.status === 404 || e.status === 410) return null; // すでに無い

          const tries = (it.tries || 0) + 1;
          if (tries >= MAX_DELETE_TRIES) {
            console.warn('カレンダーの予定を消せませんでした。あきらめます:', it.eventId);
            return null;
          }
          return { ...it, tries };
        }
      })
    )
  );

  return results.filter(Boolean);
};

/**
 * Google カレンダー → MyTaskLog
 * 【最適化】3件並列化で複数カレンダーを高速化
 */
export const importFromGoogle = async (calendarIds, dayMap) => {
  // 新しい読み込みセッションを開始するため、pace() の状態をリセット
  resetPace();

  const selected = new Set((calendarIds || []).filter(Boolean));
  if (selected.size === 0) return { dayMap, changedDates: [], missingCalendarIds: [] };

  const { from, to, fromStr, toStr } = importWindow();

  /**
   * 【最適化】複数カレンダーを3件並列化で高速化
   * 消されたカレンダーのIDが設定に残っていると 404 になるが、
   * それだけを取り除いて先へ進む。全体を止めると、
   * 画面に出ないIDのせいで同期が二度と通らなくなる。
   */
  const limit = pLimit(3);
  const results = [];
  const missingCalendarIds = [];

  const listPromises = [...selected].map((calId) =>
    limit(async () => {
      try {
        const events = await listEvents(calId, from, to);
        return { calId, events };
      } catch (e) {
        if (e.status === 404 || e.status === 410) {
          missingCalendarIds.push(calId);
          selected.delete(calId);
          return null;
        }
        throw e;
      }
    })
  );

  const listResults = await Promise.all(listPromises);
  listResults.forEach((result) => {
    if (result) results.push(result);
  });

  const next = {};
  Object.keys(dayMap).forEach((d) => {
    next[d] = [...(dayMap[d] || [])];
  });

  const changed = new Set();
  const now = Date.now();

  // ------------------------------------------------------
  // 同じイベントから作られたタスクが複数あれば、1つだけ残す。
  // 端末間の取り込みが重なると重複することがあり、
  // 放置すると照合から漏れて消えなくなるため、ここで片付ける。
  // ------------------------------------------------------
  const byKey = new Map();
  Object.keys(next).forEach((d) => {
    const dupIds = new Set();

    next[d].filter(alive).forEach((t) => {
      if (!t.googleEventId || !t.googleCalendarId) return;
      const key = keyOf(t.googleCalendarId, t.googleEventId);
      const prev = byKey.get(key);

      if (!prev) {
        byKey.set(key, { date: d, task: t });
        return;
      }

      // 先に作られたほう（idが小さいほう）を残す
      if (t.id < prev.task.id) {
        dupIds.add(prev.task.id);
        byKey.set(key, { date: d, task: t });
      } else {
        dupIds.add(t.id);
      }
    });

    if (dupIds.size > 0) {
      next[d] = next[d].map((t) =>
        dupIds.has(t.id) ? { ...t, deletedAt: now, updatedAt: now } : t
      );
      changed.add(d);
    }
  });

  const seen = new Set();

  results.forEach(({ calId, events }) => {
    const usable = events.filter(
      (e) => e.status !== 'cancelled' && e.start?.dateTime && e.end?.dateTime
    );

    usable.forEach((ev) => {
      const start = new Date(ev.start.dateTime);
      const end = new Date(ev.end.dateTime);
      const dateStr = toDateStr(start);
      if (dateStr < fromStr || dateStr > toStr) return;

      const key = keyOf(calId, ev.id);
      seen.add(key);

      const fields = {
        name: ev.summary || '(無題)',
        date: dateStr,
        scheduledStartTime: fmtHM(start),
        estimatedMinutes: Math.max(0, Math.round((end - start) / 60000)),
        // 予定の説明文は、予定タスクのメモとして持つ
        note: ev.description || '',
        googleEventId: ev.id,
        googleCalendarId: calId,
      };

      const hit = byKey.get(key);

      if (hit) {
        const { date: oldDate, task } = hit;

        // 日付が変わった場合は移動する
        if (oldDate !== dateStr) {
          next[oldDate] = (next[oldDate] || []).map((t) =>
            t.id === task.id ? { ...t, deletedAt: now, updatedAt: now } : t
          );
          const moved = { ...task, ...fields, id: newId(), updatedAt: now };
          next[dateStr] = [...(next[dateStr] || []), moved];
          byKey.set(key, { date: dateStr, task: moved });
          changed.add(oldDate);
          changed.add(dateStr);
          return;
        }

        const differs =
          task.name !== fields.name ||
          task.scheduledStartTime !== fields.scheduledStartTime ||
          task.estimatedMinutes !== fields.estimatedMinutes ||
          (task.note || '') !== fields.note;

        if (differs) {
          next[dateStr] = next[dateStr].map((t) =>
            t.id === task.id ? { ...t, ...fields, updatedAt: now } : t
          );
          changed.add(dateStr);
        }
      } else {
        const created = {
          id: newId(),
          startedAt: null,
          endedAt: null,
          isCompleted: false,
          isRunning: false,
          deletedAt: null,
          updatedAt: now,
          ...fields,
        };
        next[dateStr] = [...(next[dateStr] || []), created];
        byKey.set(key, { date: dateStr, task: created });
        changed.add(dateStr);
      }
    });
  });

  // Google側で削除された予定 / 選択から外したカレンダー由来の予定を消す
  Object.keys(next).forEach((d) => {
    if (d < fromStr || d > toStr) return;
    let touched = false;
    next[d] = next[d].map((t) => {
      if (!alive(t) || !t.googleEventId || !t.googleCalendarId) return t;
      const stillThere =
        selected.has(t.googleCalendarId) && seen.has(keyOf(t.googleCalendarId, t.googleEventId));
      if (stillThere) return t;
      touched = true;
      return { ...t, deletedAt: now, updatedAt: now };
    });
    if (touched) changed.add(d);
  });

  return { dayMap: next, changedDates: [...changed], missingCalendarIds };
};

/**
 * カレンダーに書く説明文。
 *
 *   【メモ】
 *   排水口の掃除も一緒にやった
 *   ---
 *   実績: 14:57（見積 15分）
 *
 * メモが無ければ、下の1行だけになる。
 */
const buildDescription = (task) => {
  const hasEst = task.estimatedMinutes !== null && task.estimatedMinutes !== undefined;
  const actual = secToClock(diffSeconds(task.startedAt, task.endedAt));
  const log = `実績: ${actual}${hasEst ? `（見積 ${task.estimatedMinutes}分）` : ''}`;

  const memo = (task.comment || '').trim();
  if (!memo) return log;

  return `【メモ】\n${memo}\n---\n${log}`;
};

const buildEventBody = (task) => ({
  summary: task.name || '(無題)',
  description: buildDescription(task),
  start: { dateTime: new Date(task.startedAt).toISOString() },
  end: { dateTime: new Date(task.endedAt).toISOString() },
});

/**
 * MyTaskLog → Google カレンダー（実績の書き出し）
 * 【最適化】3件並列化 + Google Calendar API の変更検出を削除
 */
export const exportToGoogle = async (calendarId, dayMap) => {
  // Google API の pace() 状態をリセット
  resetPace();

  if (!calendarId) return { dayMap, changedDates: [], count: 0, failed: 0 };

  const next = {};
  Object.keys(dayMap).forEach((d) => {
    next[d] = [...(dayMap[d] || [])];
  });

  const changed = new Set();
  let count = 0;
  let failed = 0;

  // 【最適化】全てのターゲットを集約して並列処理
  const allTargets = [];
  const dateStrMap = {}; // dateStr ごとの tasks を追跡するためのマップ

  for (const dateStr of Object.keys(next)) {
    const targets = next[dateStr].filter(
      (t) => alive(t) && t.exportPending && t.isCompleted && t.startedAt && t.endedAt
    );
    targets.forEach((task) => {
      allTargets.push({ dateStr, task });
      if (!dateStrMap[dateStr]) dateStrMap[dateStr] = [];
      dateStrMap[dateStr].push(task.id);
    });
  }

  // 3件並列化で処理
  const limit = pLimit(3);
  const updatePromises = allTargets.map((item) =>
    limit(async () => {
      const { dateStr, task } = item;
      try {
        const body = buildEventBody(task);
        let eventId = task.googleExportId;

        // 前に書き出した先と今の書き出し先が違うなら、新しく作る
        const sameCalendar = task.googleExportCalendarId
          ? task.googleExportCalendarId === calendarId
          : true;

        if (eventId && sameCalendar) {
          try {
            await patchEvent(calendarId, eventId, body);
          } catch (e) {
            // カレンダーごと作り直したなどで、そのイベントがもう無い場合は作り直す。
            if (e.status === 404 || e.status === 410) {
              const created = await insertEvent(calendarId, body);
              eventId = created.id;
            } else {
              throw e;
            }
          }
        } else {
          const created = await insertEvent(calendarId, body);
          eventId = created.id;
        }

        // 結果を記録して、後で next を更新
        return {
          success: true,
          dateStr,
          taskId: task.id,
          eventId,
        };
      } catch (e) {
        // 権限そのものが無い場合は、続けても全部失敗する。ここで止める
        if (e.code === 'auth') throw e;

        // 新規作成でも 404 なら、書き出し先のカレンダーそのものが無い
        if (e.status === 404 || e.status === 410) {
          const err = new Error('書き出し先のカレンダーが見つかりませんでした。設定で選び直してください。');
          err.code = 'output-calendar-missing';
          throw err;
        }

        // それ以外は、この1件を諦めて次へ。exportPending は残るので次回また送られる
        failed += 1;
        console.warn('カレンダーへの書き出しに失敗:', e.message);
        return {
          success: false,
          dateStr,
          taskId: task.id,
        };
      }
    })
  );

  // 全ての並列処理を待つ
  const results = await Promise.all(updatePromises);

  // 結果を反映して next を更新
  for (const result of results) {
    if (result.success) {
      const { dateStr, taskId, eventId } = result;
      // exportedAt と updatedAt を同じ時刻にしておく。
      // 次の更新で「送ってから変わっていない」と判断できるようにするため。
      const ts = Date.now();
      next[dateStr] = next[dateStr].map((t) =>
        t.id === taskId
          ? {
              ...t,
              exportPending: false,
              googleExportId: eventId,
              googleExportCalendarId: calendarId,
              exportedAt: ts,
              updatedAt: ts,
            }
          : t
      );
      changed.add(dateStr);
      count += 1;
    }
  }

  return { dayMap: next, changedDates: [...changed], count, failed };
};
